import { useAlertSubscriptions, useSetAlertSubscription } from '../../../hooks/admin/useCatchAllAlerts'

/**
 * Who gets which kinds of alert.
 *
 * Before this existed, every enabled recipient received every admin alert type.
 * The grid is opt-out: a recipient with nothing configured is subscribed to
 * everything, so nobody's alerts changed when this shipped.
 *
 * A recipient whose master switch is off receives nothing regardless of the
 * ticks here, which the row makes explicit rather than leaving the reader to
 * infer it from a greyed-out checkbox.
 */
export default function SubscriptionsPage() {
  const { data, isLoading, isError } = useAlertSubscriptions()
  const setSub = useSetAlertSubscription()

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Who Gets What</h1>
        <p className="text-gray-600 mt-1">
          Choose which kinds of alert each recipient receives. Everyone starts subscribed to
          everything; untick to stop sending a category to that person.
        </p>
      </div>

      {isLoading && <p className="text-gray-600">Loading…</p>}
      {isError && <p className="text-red-600">Could not load subscriptions.</p>}

      {data && data.recipients.length === 0 && (
        <p className="text-gray-600">
          No recipients yet. Add one on the <strong>Recipients</strong> page first.
        </p>
      )}

      {data && data.recipients.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200 text-left">
                <th className="py-3 px-4 font-medium text-gray-600">Recipient</th>
                {data.categories.map((c) => (
                  <th key={c.category} className="py-3 px-4 font-medium text-gray-600 text-center">
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.recipients.map((r) => (
                <tr key={r.id} className={r.enabled ? '' : 'bg-gray-50'}>
                  <td className="py-3 px-4">
                    <div className="font-medium">{r.label || r.contact}</div>
                    <div className="text-sm text-gray-500">
                      {r.label ? `${r.contact} · ` : ''}
                      {r.contactType}
                    </div>
                    {!r.enabled && (
                      <div className="text-xs text-amber-700 mt-1">
                        Recipient disabled — receives nothing regardless of these
                      </div>
                    )}
                  </td>
                  {data.categories.map((c) => (
                    <td key={c.category} className="py-3 px-4 text-center">
                      <input
                        type="checkbox"
                        aria-label={`${c.label} for ${r.label || r.contact}`}
                        checked={r.subscriptions[c.category]}
                        disabled={setSub.isPending || !r.enabled}
                        onChange={(e) =>
                          setSub.mutate({
                            recipientId: r.id,
                            category: c.category,
                            subscribed: e.target.checked,
                          })
                        }
                        className="rounded border-gray-300 h-5 w-5"
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-sm text-gray-500">
        System-health alerts are not listed: they use their own separate recipient list.
      </p>
    </div>
  )
}
